import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button, Input, Select, Textarea } from "./ui";
import { Modal } from "./feedback";
import { api } from "../services/api";
import { toastSuccess, useUi } from "../stores/ui";
import { activityLabel } from "../utils/activityLabel";
import { formatWaitTime, waitMsSince } from "../utils/waitTime";
import type { Recruit, RosterMember } from "../types";
import { cn } from "../utils/cn";

export function LogActivityModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => api<{ maxQuantityPerSubmission: number; notesRequired: boolean; notesMaxLength: number }>(
      "/api/settings/public",
    ),
  });
  const eligibility = useQuery({
    queryKey: ["eligibility"],
    queryFn: () => api<{ types: Array<"INTERVIEW" | "TRAINING"> }>("/api/activity/eligibility"),
    enabled: open,
  });
  const types = eligibility.data?.types ?? ["INTERVIEW", "TRAINING"];
  const [type, setType] = useState<"INTERVIEW" | "TRAINING">(types[0] ?? "INTERVIEW");
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [recruitQuery, setRecruitQuery] = useState("");
  const [selectedRecruits, setSelectedRecruits] = useState<Recruit[]>([]);
  const [assistantQuery, setAssistantQuery] = useState("");
  const [selectedAssistants, setSelectedAssistants] = useState<RosterMember[]>([]);
  const [step, setStep] = useState(1);
  const max = settings.data?.maxQuantityPerSubmission ?? 10;
  const user = useUi((s) => s.user);

  const selectedType = useMemo(() => (types.length === 1 ? types[0] : type), [types, type]);
  const recruits = useQuery({
    queryKey: ["recruits", "log"],
    queryFn: () => api<{ items: Recruit[] }>("/api/recruits"),
    enabled: open && selectedType === "TRAINING",
  });
  const trainers = useQuery({
    queryKey: ["training-members"],
    queryFn: () => api<{ members: RosterMember[] }>("/api/activity/training-members"),
    enabled: open && selectedType === "TRAINING",
  });

  const selectedIds = new Set(selectedRecruits.map((item) => item.discordId));
  const recruitMatches = (recruits.data?.items ?? []).filter((item) => {
    if (selectedIds.has(item.discordId)) return false;
    const query = recruitQuery.trim().toLowerCase();
    if (!query) return true;
    return [item.displayName, item.username, item.nickname, item.globalName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  });
  const selectedAssistantIds = new Set(selectedAssistants.map((item) => item.discordId));
  const assistantMatches = (trainers.data?.members ?? []).filter((item) => {
    if (!item.discordId || item.discordId === user?.discordId || selectedAssistantIds.has(item.discordId)) return false;
    const query = assistantQuery.trim().toLowerCase();
    if (!query) return true;
    return [item.displayName, item.username, item.nickname, item.globalName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  });

  const mutation = useMutation({
    mutationFn: () =>
      api("/api/activity", {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({
          type: selectedType,
          quantity,
          notes,
          recruitDiscordIds: selectedType === "TRAINING" ? selectedRecruits.map((item) => item.discordId) : [],
          assistantDiscordIds: selectedType === "TRAINING" ? selectedAssistants.map((item) => item.discordId).filter(Boolean) : [],
        }),
      }),
    onSuccess: async () => {
      toastSuccess("Activity logged", `${quantity} ${activityLabel(selectedType, quantity).toLowerCase()} saved.`);
      await queryClient.invalidateQueries();
      setStep(1);
      setQuantity(1);
      setNotes("");
      setRecruitQuery("");
      setSelectedRecruits([]);
      setAssistantQuery("");
      setSelectedAssistants([]);
      onClose();
    },
  });

  function resetAndClose() {
    setStep(1);
    setRecruitQuery("");
    setAssistantQuery("");
    onClose();
  }

  return (
    <Modal open={open} title="Log Activity" onClose={resetAndClose}>
      {step === 1 ? (
        <div className="space-y-4">
          <p className="text-sm text-zinc-400">Select the type of HR activity you hosted.</p>
          {types.length === 1 ? (
            <div className="rounded-md border border-white/10 bg-ink-950 p-3 font-medium">{types[0] === "INTERVIEW" ? "Interview" : "Training"}</div>
          ) : (
            <Select value={selectedType} onChange={(e) => setType(e.target.value as "INTERVIEW" | "TRAINING")}>
              {types.map((item) => (
                <option key={item} value={item}>
                  {item === "INTERVIEW" ? "Interview" : "Training"}
                </option>
              ))}
            </Select>
          )}
          <Button onClick={() => setStep(2)}>Continue</Button>
        </div>
      ) : null}
      {step === 2 ? (
        <div className="space-y-4">
          <p className="text-3xl font-semibold tracking-tight">
            {quantity} {activityLabel(selectedType, quantity)}
          </p>
          <input
            type="range"
            min={1}
            max={max}
            value={quantity}
            aria-label="Quantity"
            className="w-full"
            onChange={(e) => setQuantity(Number(e.target.value))}
          />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setQuantity(Math.max(1, quantity - 1))} aria-label="Decrease quantity">
              −
            </Button>
            <Button variant="ghost" onClick={() => setQuantity(Math.min(max, quantity + 1))} aria-label="Increase quantity">
              +
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(1)}>
              Back
            </Button>
            <Button onClick={() => setStep(3)}>Continue</Button>
          </div>
        </div>
      ) : null}
      {step === 3 ? (
        <div className="space-y-4">
          {selectedType === "TRAINING" ? (
            <div className="space-y-2">
              <p className="text-sm text-zinc-400">Add the recruits who received this training.</p>
              {selectedRecruits.length ? (
                <div className="flex flex-wrap gap-2">
                  {selectedRecruits.map((item) => (
                    <button
                      key={item.discordId}
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-full border border-accent/40 bg-accent/15 px-2.5 py-1 text-xs text-white"
                      onClick={() => setSelectedRecruits((current) => current.filter((recruit) => recruit.discordId !== item.discordId))}
                    >
                      {item.displayName}
                      <X size={12} />
                    </button>
                  ))}
                </div>
              ) : null}
              <Input
                value={recruitQuery}
                onChange={(e) => setRecruitQuery(e.target.value)}
                placeholder="Search awaiting recruits"
              />
              <div className="max-h-48 overflow-auto rounded-md border border-white/10 bg-ink-950">
                {recruits.isLoading ? (
                  <p className="px-3 py-2 text-sm text-zinc-500">Loading recruits…</p>
                ) : recruitMatches.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-zinc-500">
                    {recruitQuery.trim() ? "No matching recruits." : "No recruits are awaiting training."}
                  </p>
                ) : (
                  recruitMatches.slice(0, 20).map((item) => (
                    <button
                      key={item.discordId}
                      type="button"
                      className={cn("flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.04]")}
                      onClick={() => {
                        setSelectedRecruits((current) => [...current, item]);
                        setRecruitQuery("");
                      }}
                    >
                      <img src={item.avatarUrl} alt="" className="h-8 w-8 rounded-full" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{item.displayName}</span>
                        <span className="block truncate text-xs text-zinc-500">@{item.username}</span>
                      </span>
                      <span className="shrink-0 text-xs text-accent">{formatWaitTime(waitMsSince(item.awaitingSince))}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}
          {selectedType === "TRAINING" ? (
            <div className="space-y-2">
              <p className="text-sm text-zinc-400">Assisting Members</p>
              {selectedAssistants.length ? (
                <div className="flex flex-wrap gap-2">
                  {selectedAssistants.map((item) => (
                    <button
                      key={item.discordId}
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.06] px-2.5 py-1 text-xs text-white"
                      onClick={() => setSelectedAssistants((current) => current.filter((member) => member.discordId !== item.discordId))}
                    >
                      {item.displayName}
                      <X size={12} />
                    </button>
                  ))}
                </div>
              ) : null}
              <Input
                value={assistantQuery}
                onChange={(e) => setAssistantQuery(e.target.value)}
                placeholder="Search training team members"
              />
              <div className="max-h-48 overflow-auto rounded-md border border-white/10 bg-ink-950">
                {trainers.isLoading ? (
                  <p className="px-3 py-2 text-sm text-zinc-500">Loading training team…</p>
                ) : assistantMatches.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-zinc-500">
                    {assistantQuery.trim() ? "No matching trainers." : "No other training team members available."}
                  </p>
                ) : (
                  assistantMatches.slice(0, 20).map((item) => (
                    <button
                      key={item.discordId}
                      type="button"
                      className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm hover:bg-white/[0.04]"
                      onClick={() => {
                        setSelectedAssistants((current) => [...current, item]);
                        setAssistantQuery("");
                      }}
                    >
                      <img src={item.avatarUrl} alt="" className="h-8 w-8 rounded-full" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">{item.displayName}</span>
                        <span className="block truncate text-xs text-zinc-500">@{item.username}</span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>
          ) : null}
          <label className="text-sm text-zinc-400" htmlFor="notes">
            Notes {settings.data?.notesRequired ? "(required)" : "(optional)"}
          </label>
          <Textarea
            id="notes"
            maxLength={settings.data?.notesMaxLength ?? 500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={selectedType === "TRAINING" ? "Hosted department training." : "Hosted academy interview session."}
          />
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(2)}>
              Back
            </Button>
            <Button onClick={() => setStep(4)}>Review</Button>
          </div>
        </div>
      ) : null}
      {step === 4 ? (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <dt className="text-zinc-400">Type</dt>
              <dd className="font-semibold">{selectedType}</dd>
            </div>
            <div>
              <dt className="text-zinc-400">Quantity</dt>
              <dd className="font-semibold">{quantity} {activityLabel(selectedType, quantity)}</dd>
            </div>
            <div>
              <dt className="text-zinc-400">Date</dt>
              <dd className="font-semibold">{new Date().toLocaleString()}</dd>
            </div>
            <div>
              <dt className="text-zinc-400">Member</dt>
              <dd className="font-semibold">{user?.displayName}</dd>
            </div>
          </dl>
          {selectedType === "TRAINING" ? (
            <div className="text-sm">
              <p className="text-zinc-400">Recruits</p>
              <p className="font-semibold">
                {selectedRecruits.length
                  ? selectedRecruits.map((item) => item.displayName).join(", ")
                  : "None selected"}
              </p>
              <p className="mt-3 text-zinc-400">Assisting Members</p>
              <p className="font-semibold">
                {selectedAssistants.length
                  ? selectedAssistants.map((item) => item.displayName).join(", ")
                  : "None selected"}
              </p>
            </div>
          ) : null}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setStep(3)}>
              Back
            </Button>
            <Button disabled={mutation.isPending} onClick={() => mutation.mutate()}>
              {mutation.isPending ? "Saving…" : "Confirm"}
            </Button>
          </div>
        </div>
      ) : null}
      {step !== 2 ? <Input className="hidden" readOnly /> : null}
    </Modal>
  );
}
